// Roofing project math: footprint area × pitch factor → squares → bundles.
// 1 square = 100 sq ft of actual roof. Architectural shingles: 4 bundles/square.
import { ceilTo, round } from '../lib/format'
import type { AccessoryLine } from '../data/companions'
import type { Locale } from '../i18n/locale'

export type RoofPitch = '3/12' | '4/12' | '5/12' | '6/12' | '7/12' | '8/12' | '9/12' | '10/12' | '12/12'

export interface RoofingInputs {
  footprintLengthFt: number
  footprintWidthFt: number
  pitch: RoofPitch
  wastePct: number             // 10-20, default 15
  needIceWater: boolean        // ice & water shield at eaves
  bundlesPerSquare: 3 | 4      // 3 = 3-tab, 4 = architectural/dimensional
}

export interface RoofingResult {
  footprintArea: number        // sq ft
  actualRoofArea: number       // sq ft (footprint × pitch factor)
  squares: number              // actual area / 100
  bundles: number              // squares × bundlesPerSquare with waste
  underlaymentRolls: number    // 1 roll per 400 sq ft
  dripEdgeLinFt: number        // perimeter of footprint
  ridgeCapBundles: number      // ridge length / 35 lin ft per bundle
  roofingNailLbs: number       // 2 lbs per square
  iceWaterSqFt: number         // if checked: eave perimeter × 3 ft width
  lines: AccessoryLine[]
}

// Pitch factors: actual slope length / run length
export const PITCH_FACTORS: Record<RoofPitch, number> = {
  '3/12': 1.031,
  '4/12': 1.054,
  '5/12': 1.083,
  '6/12': 1.118,
  '7/12': 1.157,
  '8/12': 1.202,
  '9/12': 1.250,
  '10/12': 1.302,
  '12/12': 1.414,
}

export const DEFAULT_ROOFING: RoofingInputs = {
  footprintLengthFt: 40,
  footprintWidthFt: 30,
  pitch: '5/12',
  wastePct: 15,
  needIceWater: true,
  bundlesPerSquare: 4,
}

const UNDERLAYMENT_SQFT_PER_ROLL = 400
const NAILS_LBS_PER_SQUARE = 2
const RIDGE_CAP_LIN_FT_PER_BUNDLE = 35

export function computeRoofing(i: RoofingInputs, locale: Locale = 'en'): RoofingResult {
  const tr = (en: string, fr: string) => (locale === 'fr' ? fr : en)
  const lFt = Math.max(0, i.footprintLengthFt)
  const wFt = Math.max(0, i.footprintWidthFt)
  const footprintArea = lFt * wFt

  const pitchFactor = PITCH_FACTORS[i.pitch] ?? 1.083
  const actualRoofArea = footprintArea * pitchFactor * 2  // multiply by 2 for both roof faces (gable)

  const areaWithWaste = actualRoofArea * (1 + Math.max(0, i.wastePct) / 100)
  const squares = footprintArea > 0 ? areaWithWaste / 100 : 0
  const bundles = footprintArea > 0 ? ceilTo(squares * i.bundlesPerSquare, 1) : 0

  const underlaymentRolls = footprintArea > 0
    ? Math.max(1, ceilTo(areaWithWaste / UNDERLAYMENT_SQFT_PER_ROLL, 1))
    : 0

  // Drip edge: perimeter of the footprint (both eaves + both rakes, approximated as rectangle)
  const dripEdgeLinFt = footprintArea > 0 ? round(2 * (lFt + wFt), 0) : 0

  // Ridge cap: ridge length = footprint width direction / 2 sides... actually ridge = length (the peak runs along the length)
  const ridgeLinFt = lFt   // ridge runs along the length dimension
  const ridgeCapBundles = footprintArea > 0 ? Math.max(1, ceilTo(ridgeLinFt / RIDGE_CAP_LIN_FT_PER_BUNDLE, 1)) : 0

  const roofingNailLbs = footprintArea > 0 ? Math.max(1, ceilTo(squares * NAILS_LBS_PER_SQUARE, 1)) : 0

  // Ice & water shield: eaves = 2 × footprint width (both eaves), 3 ft width from edge
  const iceWaterSqFt = i.needIceWater && footprintArea > 0
    ? round(wFt * 2 * 3, 0)   // 3 ft wide strip along each eave
    : 0
  const iceWaterRolls = iceWaterSqFt > 0 ? Math.max(1, ceilTo(iceWaterSqFt / 200, 1)) : 0  // ~200 sq ft per roll

  const lines: AccessoryLine[] = []

  lines.push({
    item: 'roofing felt underlayment 15 lb synthetic roll',
    label: tr('Roofing underlayment', 'Sous-couche de toiture'),
    kind: 'consumable',
    qty: underlaymentRolls,
    unit: 'roll',
    note: tr(`~${UNDERLAYMENT_SQFT_PER_ROLL} sq ft per roll · synthetic felt preferred over 15 lb paper for durability`, `~${UNDERLAYMENT_SQFT_PER_ROLL} pi² par rouleau · le feutre synthétique est préférable au papier 15 lb pour la durabilité`),
  })

  lines.push({
    item: 'drip edge aluminum galvanized roofing',
    label: tr('Drip edge', 'Larmier'),
    kind: 'consumable',
    qty: Math.max(1, ceilTo(dripEdgeLinFt / 10, 1)),
    unit: 'piece',
    note: tr(`10 ft pieces · ${dripEdgeLinFt} lin ft perimeter · install before underlayment on eaves, after on rakes`, `Pièces de 10 pi · ${dripEdgeLinFt} pi linéaires de périmètre · posez-le avant la sous-couche aux avant-toits, après aux rives`),
  })

  lines.push({
    item: 'ridge cap shingles bundle roofing',
    label: tr('Ridge cap bundles', 'Paquets de bardeaux de faîte'),
    kind: 'consumable',
    qty: ridgeCapBundles,
    unit: 'bundle',
    note: tr(`~${RIDGE_CAP_LIN_FT_PER_BUNDLE} lin ft per bundle · use pre-cut hip & ridge shingles for the best seal`, `~${RIDGE_CAP_LIN_FT_PER_BUNDLE} pi linéaires par paquet · utilisez des bardeaux d’arête et de faîte pré-coupés pour le meilleur scellement`),
  })

  lines.push({
    item: 'coil roofing nails 1-3/4 galvanized',
    label: tr('Roofing nails', 'Clous de toiture'),
    kind: 'consumable',
    qty: roofingNailLbs,
    unit: 'lb',
    note: tr(`${NAILS_LBS_PER_SQUARE} lbs per square · 1-3/4 in galvanized coil nails for pneumatic nailer`, `${NAILS_LBS_PER_SQUARE} lb par carré · clous en rouleau galvanisés de 1-3/4 po pour cloueuse pneumatique`),
  })

  if (i.needIceWater) {
    lines.push({
      item: 'ice and water shield self-adhesive roofing',
      label: tr('Ice & water shield', 'Membrane pare-glace et pare-eau'),
      kind: 'consumable',
      qty: iceWaterRolls,
      unit: 'roll',
      note: tr(`Self-adhesive membrane · covers ${iceWaterSqFt} sq ft of eaves (~${iceWaterRolls} × 200 sq ft rolls) · required in cold climates`, `Membrane autocollante · couvre ${iceWaterSqFt} pi² d’avant-toits (~${iceWaterRolls} rouleaux × 200 pi²) · exigée dans les climats froids`),
    })
  } else {
    lines.push({
      item: 'ice and water shield self-adhesive roofing',
      label: tr('Ice & water shield', 'Membrane pare-glace et pare-eau'),
      kind: 'consumable',
      qty: 0,
      unit: 'roll',
      note: tr('Check the option above if in a cold climate — required by code in many regions', 'Cochez l’option ci-dessus si vous êtes en climat froid — exigée par le code dans plusieurs régions'),
      optional: true,
    })
  }

  lines.push({
    item: 'pneumatic roofing nailer coil nail gun',
    label: tr('Roofing nailer', 'Cloueuse de toiture'),
    kind: 'tool',
    note: tr('Coil nailer dramatically speeds up shingle installation — rent or own.', 'La cloueuse à rouleau accélère énormément la pose des bardeaux — à louer ou à acheter.'),
    optional: true,
  })

  lines.push({ item: 'roofing harness safety kit fall protection', label: tr('Safety harness', 'Harnais de sécurité'), kind: 'tool', note: tr('Fall arrest is mandatory for roof work — rent or buy before starting.', 'Le dispositif antichute est obligatoire pour les travaux de toiture — louez ou achetez avant de commencer.') })
  lines.push({ item: 'roofing caulk sealant flashing', label: tr('Roofing caulk / sealant', 'Ciment à toiture / scellant'), kind: 'consumable', qty: Math.max(1, ceilTo(dripEdgeLinFt / 150, 1)), unit: 'tube', note: tr('For flashing, step flashing, chimney and vent pipe sealing', 'Pour les solins, les solins à gradins, et le scellement des cheminées et des tuyaux d’évent'), optional: true })

  return {
    footprintArea: round(footprintArea, 0),
    actualRoofArea: round(actualRoofArea, 0),
    squares: round(squares, 2),
    bundles,
    underlaymentRolls,
    dripEdgeLinFt: round(dripEdgeLinFt, 0),
    ridgeCapBundles,
    roofingNailLbs,
    iceWaterSqFt,
    lines,
  }
}
